import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Public as WorldIcon,
  ThreeDRotation,
  CloudUpload,
} from '@mui/icons-material';
import { api, endpoints } from '../config/api';
import { VirtualWorld, Object3D } from '../types';

const VirtualWorlds: React.FC = () => {
  const [worlds, setWorlds] = useState<VirtualWorld[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [objectDialogOpen, setObjectDialogOpen] = useState(false);
  const [selectedWorld, setSelectedWorld] = useState<VirtualWorld | null>(null);
  const [selectedObject, setSelectedObject] = useState<Object3D | null>(null);
  const [formData, setFormData] = useState<Partial<VirtualWorld>>({
    name: '',
    description: '',
    skyboxUrl: '',
    skyboxDropboxPath: '',
    objects: [],
    lighting: {
      ambient: [0.3, 0.3, 0.3],
      directional: {
        direction: [0.5, -1.0, 0.3],
        color: [1.0, 0.9, 0.8],
      },
    },
    portalPosition: [0, 1.2, 0],
    portalRotation: [0, 0, 0],
  });
  const [objectFormData, setObjectFormData] = useState<Partial<Object3D>>({
    type: 'sphere',
    name: '',
    modelUrl: '',
    modelDropboxPath: '',
    position: [0, 0, 0],
    rotation: [0, 0, 0],
    scale: [1, 1, 1],
    color: '#FFFFFF',
  });

  useEffect(() => {
    fetchWorlds();
  }, []);

  const fetchWorlds = async () => {
    try {
      setLoading(true);
      const response = await api.get(endpoints.virtualWorlds);
      setWorlds(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch virtual worlds');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedWorld(null);
    setFormData({
      name: '',
      description: '',
      skyboxUrl: '',
      skyboxDropboxPath: '',
      objects: [],
      lighting: {
        ambient: [0.3, 0.3, 0.3],
        directional: {
          direction: [0.5, -1.0, 0.3],
          color: [1.0, 0.9, 0.8],
        },
      },
      portalPosition: [0, 1.2, 0],
      portalRotation: [0, 0, 0],
    });
    setDialogOpen(true);
  };

  const handleEdit = (world: VirtualWorld) => {
    setSelectedWorld(world);
    setFormData({
      name: world.name,
      description: world.description,
      skyboxUrl: world.skyboxUrl,
      skyboxDropboxPath: world.skyboxDropboxPath,
      objects: world.objects || [],
      lighting: world.lighting,
      portalPosition: world.portalPosition,
      portalRotation: world.portalRotation,
    });
    setDialogOpen(true);
  };

  const handleDelete = (world: VirtualWorld) => {
    setSelectedWorld(world);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (selectedWorld) {
        await api.put(endpoints.virtualWorld(selectedWorld.id), formData);
        setSuccess('Virtual world updated successfully');
      } else {
        await api.post(endpoints.virtualWorlds, formData);
        setSuccess('Virtual world created successfully');
      }
      setDialogOpen(false);
      fetchWorlds();
    } catch (err) {
      setError('Failed to save virtual world');
      console.error(err);
    }
  };

  const confirmDelete = async () => {
    if (selectedWorld) {
      try {
        await api.delete(endpoints.virtualWorld(selectedWorld.id));
        setSuccess('Virtual world deleted successfully');
        setDeleteDialogOpen(false);
        fetchWorlds();
      } catch (err) {
        setError('Failed to delete virtual world');
        console.error(err);
      }
    }
  };

  const handleAddObject = () => {
    setSelectedObject(null);
    setObjectFormData({
      type: 'sphere',
      name: '',
      modelUrl: '',
      modelDropboxPath: '',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      color: '#FFFFFF',
    });
    setObjectDialogOpen(true);
  };

  const handleEditObject = (object: Object3D, index: number) => {
    setSelectedObject(object);
    setObjectFormData(object);
    setObjectDialogOpen(true);
  };

  const handleSaveObject = () => {
    const newObject = objectFormData as Object3D;
    if (selectedObject) {
      // Edit existing object
      const objects = formData.objects || [];
      const index = objects.findIndex(o => o === selectedObject);
      objects[index] = newObject;
      setFormData({ ...formData, objects });
    } else {
      // Add new object
      setFormData({ ...formData, objects: [...(formData.objects || []), newObject] });
    }
    setObjectDialogOpen(false);
  };

  const handleDeleteObject = (index: number) => {
    const objects = formData.objects || [];
    objects.splice(index, 1);
    setFormData({ ...formData, objects });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading virtual worlds...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Virtual Worlds</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Create Virtual World
        </Button>
      </Box>

      {worlds.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" align="center">
              No virtual worlds found. Create your first virtual world to get started.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {worlds.map((world) => (
            <Grid size={{ xs: 12, md: 6 }} key={world.id}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <WorldIcon />
                    <Typography variant="h6">{world.name}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {world.description || 'No description'}
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip
                      icon={<ThreeDRotation />}
                      label={`${world.objects.length} objects`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={world.createdBy || 'Unknown'}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  {world.objects.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        Objects:
                      </Typography>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {world.objects.map((obj, index) => (
                          <Chip key={index} label={obj.name || obj.type} size="small" />
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>
                <CardActions>
                  <IconButton onClick={() => handleEdit(world)} title="Edit">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(world)} title="Delete">
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit World Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedWorld ? 'Edit Virtual World' : 'Create Virtual World'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="World Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Skybox URL"
                value={formData.skyboxUrl}
                onChange={(e) => setFormData({ ...formData, skyboxUrl: e.target.value })}
                required
              />
            </Grid>
            <Grid size={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1">3D Objects</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={handleAddObject}>
                  Add Object
                </Button>
              </Box>
              {formData.objects && formData.objects.length > 0 ? (
                <List>
                  {formData.objects.map((obj, index) => (
                    <Paper key={index} sx={{ mb: 1 }}>
                      <ListItem>
                        <ListItemText
                          primary={obj.name || `${obj.type} Object`}
                          secondary={`Position: [${obj.position.join(', ')}] | Scale: [${obj.scale.join(', ')}]`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton onClick={() => handleEditObject(obj, index)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteObject(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    </Paper>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No objects added yet
                </Typography>
              )}
            </Grid>
            <Grid size={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Portal Settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label="Portal Position (x, y, z)"
                        value={formData.portalPosition?.join(', ')}
                        onChange={(e) => {
                          const values = e.target.value.split(',').map(v => parseFloat(v.trim()) || 0);
                          if (values.length === 3) {
                            setFormData({ ...formData, portalPosition: values });
                          }
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label="Portal Rotation (x, y, z)"
                        value={formData.portalRotation?.join(', ')}
                        onChange={(e) => {
                          const values = e.target.value.split(',').map(v => parseFloat(v.trim()) || 0);
                          if (values.length === 3) {
                            setFormData({ ...formData, portalRotation: values });
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name || !formData.skyboxUrl}
          >
            {selectedWorld ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Object Dialog */}
      <Dialog open={objectDialogOpen} onClose={() => setObjectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedObject ? 'Edit Object' : 'Add Object'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Object Type</InputLabel>
                <Select
                  value={objectFormData.type}
                  label="Object Type"
                  onChange={(e) => setObjectFormData({ ...objectFormData, type: e.target.value as 'sphere' | 'cube' | 'model' })}
                >
                  <MenuItem value="sphere">Sphere</MenuItem>
                  <MenuItem value="cube">Cube</MenuItem>
                  <MenuItem value="model">3D Model</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Object Name"
                value={objectFormData.name}
                onChange={(e) => setObjectFormData({ ...objectFormData, name: e.target.value })}
                required
              />
            </Grid>
            {objectFormData.type === 'model' && (
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Model URL"
                  value={objectFormData.modelUrl}
                  onChange={(e) => setObjectFormData({ ...objectFormData, modelUrl: e.target.value })}
                  required
                />
              </Grid>
            )}
            {objectFormData.type !== 'model' && (
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Color"
                  type="color"
                  value={objectFormData.color}
                  onChange={(e) => setObjectFormData({ ...objectFormData, color: e.target.value })}
                />
              </Grid>
            )}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Position (x, y, z)"
                value={objectFormData.position?.join(', ')}
                onChange={(e) => {
                  const values = e.target.value.split(',').map(v => parseFloat(v.trim()) || 0);
                  if (values.length === 3) {
                    setObjectFormData({ ...objectFormData, position: values });
                  }
                }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Rotation (x, y, z)"
                value={objectFormData.rotation?.join(', ')}
                onChange={(e) => {
                  const values = e.target.value.split(',').map(v => parseFloat(v.trim()) || 0);
                  if (values.length === 3) {
                    setObjectFormData({ ...objectFormData, rotation: values });
                  }
                }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Scale (x, y, z)"
                value={objectFormData.scale?.join(', ')}
                onChange={(e) => {
                  const values = e.target.value.split(',').map(v => parseFloat(v.trim()) || 1);
                  if (values.length === 3) {
                    setObjectFormData({ ...objectFormData, scale: values });
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setObjectDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveObject}
            variant="contained"
            disabled={!objectFormData.name}
          >
            {selectedObject ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the virtual world "{selectedWorld?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')}>
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VirtualWorlds;